import { useClaimAccount } from '../lib/useClaimAccount'
import {
  DEFAULT_APP_PATH,
  signInWithGoogle,
  useConfig,
} from '../lib/auth-client'

type Props = {
  callbackURL?: string
  requireIdentity?: boolean
  title?: string
  subtitle?: string
  onDone?: () => void
}

/** Shared Google + magic-link auth UI. */
export function AuthMethods({
  callbackURL = DEFAULT_APP_PATH,
  requireIdentity = false,
  title,
  subtitle,
  onDone,
}: Props) {
  const claim = useClaimAccount({
    callbackURL,
    onLinked: onDone,
  })
  const { data: config } = useConfig()
  const googleEnabled = config?.googleEnabled ?? false

  return (
    <div className="space-y-4">
      {(title || subtitle) && (
        <div>
          {title && <h2 className="text-xl font-semibold">{title}</h2>}
          {subtitle && (
            <p className="text-sm text-green-800/80 mt-1">{subtitle}</p>
          )}
        </div>
      )}

      {googleEnabled && (
        <button
          type="button"
          disabled={claim.busy}
          onClick={() => signInWithGoogle(callbackURL)}
          className="w-full rounded-full bg-white border border-green-300 hover:bg-green-100 text-green-950 py-2.5 flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <GoogleGlyph />
          Doorgaan met Google
        </button>
      )}

      {!claim.sent ? (
        <form onSubmit={claim.requestMagicLink} className="space-y-3">
          <label className="block text-sm font-medium">
            E-mailadres
            <input
              type="email"
              required
              autoComplete="email"
              value={claim.email}
              onChange={(e) => claim.setEmail(e.target.value)}
              className="mt-1 w-full rounded-lg border border-green-200 bg-white px-3 py-2"
              placeholder="jij@voorbeeld.be"
            />
          </label>
          <button
            type="submit"
            disabled={claim.busy}
            className="w-full rounded-full bg-green-700 hover:bg-green-800 text-white py-2.5 disabled:opacity-60"
          >
            {claim.busy ? 'Bezig…' : 'Stuur inloglink'}
          </button>
          <p className="text-xs text-green-800/70">
            We sturen een inloglink naar je e-mail
            {requireIdentity ? '.' : '.'}
          </p>
        </form>
      ) : (
        <div className="space-y-3">
          <p className="text-sm">
            Check je inbox voor <strong>{claim.email}</strong> en open de
            inloglink.
          </p>
          <button
            type="button"
            onClick={() => claim.setSent(false)}
            className="w-full text-sm text-green-800/70 underline"
          >
            Ander e-mailadres
          </button>
        </div>
      )}
    </div>
  )
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.5-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.3 35.3 26.8 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.4 39.6 16.1 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l.1.1 6.2 5.2C39.2 36.3 44 31 44 24c0-1.3-.1-2.5-.4-3.5z"
      />
    </svg>
  )
}
